import React, { useEffect, useRef, useState } from "react";
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import SaveIcon from '@mui/icons-material/Save';
import InfoIcon from '@mui/icons-material/Info';
import UndoIcon from '@mui/icons-material/Undo';
import ErrorIcon from "@mui/icons-material/Error";
import BlackLogo from '../../../../../icon.logo.black.png';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from "@mui/styles";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CircularProgress from "@mui/material/CircularProgress";
import SignalWifiBadIcon from '@mui/icons-material/SignalWifiBad';
import { selectUserRole } from "../../../../../slices/userInfoSlice";
import MiscellaneousServicesIcon from '@mui/icons-material/MiscellaneousServices';
import { useDispatch, useSelector } from "react-redux";
import { selectUserDefaultLocations } from "../../../../../slices/teamAndUserSettingsSlice";
import { selectTeamRemainingMonthlyQueries } from "../../../../../slices/teamStatsSlice";
import { selectSaveFromSearchPerm, selectSearchForPropertyPerm } from "../../../../../slices/userActionsPermissionsSlice";
import { clearPropertySearchResponse, selectViewingMortgageFromSearch } from "../../../../../slices/viewingSearchResultsSlice";
import { AccentInfo, AlertWrapper, Button, ButtonWrapper, Divider, ErrorWrapper, FlexWrapper, HeaderText, ImageElements, InfoBubble, InputWrapper, Label, StatusAndLabelWrapper, StyledInputElement, TreeContainer, Typography } from "../../../../../StyledComponents";

const SearchSideBar = ({ allFieldsPresent, searchFetching, handlePropertySearch, responseError, setPropertyNotFound, setResponseError, propertyNotFound, nullForm, setNullForm, handleSaveProperty, saveError, existingMortgage, saveSuccess, handleShowAll, showAll, saveFunctionEnabled, handleMortgageScan }) => {
  const theme = useTheme()
  const dispatch = useDispatch()
  
  const userRole = useSelector(selectUserRole)
  const saveFromSearchPerm = useSelector(selectSaveFromSearchPerm)
  const userDefaultLocations = useSelector(selectUserDefaultLocations)
  const searchForPropertyPerm = useSelector(selectSearchForPropertyPerm)
  const remainingMonthlyQueries = useSelector(selectTeamRemainingMonthlyQueries)
  const viewingMortgageFromSearch = useSelector(selectViewingMortgageFromSearch)
  
  const [statusStreetAddress, setStatusStreetAddress] = useState(null)
  const streetAddress = useRef()
  const [statusCity, setStatusCity] = useState(null)
  const city = useRef()
  const [statusState, setStatusState] = useState(null)
  const state = useRef()
  const [statusPostalCode, ssetStatusPostalCode] = useState(null)
  const postalCode = useRef()

  const validateSearch = () => {
    setNullForm(false)
    setResponseError(false)
    setPropertyNotFound(false)
    let newPostalCode = parseFloat(postalCode.current.value)
    let negativePostalCode = Math.sign(newPostalCode)
    if (streetAddress.current.value.includes('<') || streetAddress.current.value.includes('>') || city.current.value.includes('<') || city.current.value.includes('>') || state.current.value.includes('<') || state.current.value.includes('>') || newPostalCode > 99999 || postalCode.current.value.includes('>') || postalCode.current.value.includes('<') || negativePostalCode === -1 || (isNaN(postalCode.current.value) && postalCode.current.value.length !== 0) || (streetAddress.current.value.length === 0 && city.current.value.length === 0 && state.current.value.length === 0 && postalCode.current.value.length === 0)) {
      if (streetAddress.current.value.includes('<') || streetAddress.current.value.includes('>')) {
        setStatusStreetAddress('invalid')
      }
      if (city.current.value.includes('<') || city.current.value.includes('>')) {
        setStatusCity('invalid')
      }
      if (state.current.value.includes('<') || state.current.value.includes('>')) {
        setStatusState('invalid')
      }
      if (newPostalCode > 99999 || postalCode.current.value.includes('>') || postalCode.current.value.includes('<') || negativePostalCode === -1 || isNaN(postalCode.current.value)) {
        ssetStatusPostalCode('invalid')
      }
      if ((streetAddress.current.value.length === 0 && city.current.value.length === 0 && state.current.value.length === 0 && postalCode.current.value.length === 0)) {
        setNullForm(true)
      }
    } else {
      handlePropertySearch(streetAddress.current.value, city.current.value, state.current.value, postalCode.current.value)
    }
  }

  useEffect(() => {
    if (userDefaultLocations.state) {
      state.current.value = userDefaultLocations.state
    }
    if (userDefaultLocations.city) {
      city.current.value = userDefaultLocations.city
    }
  }, [userDefaultLocations])

  return (
    <>
      <FlexWrapper style={{gridColumn: '1',gridRow: '1', paddingLeft: '2rem'}}>
        <ImageElements src={BlackLogo} style={{width: '2.5rem', height: '2.5rem', opacity: '0.5', margin: 'auto 0.5rem auto 0'}} />
        <Typography className="xxLarge secondary alignCenter" style={{height: '100%', paddingBottom: '0.1rem'}}>Lancaster Search</Typography>
      </FlexWrapper>
      <FlexWrapper className="column justifyBetween" style={{gridColumn: '1', gridRow: '2', height: '100%'}}>
        <Divider style={{height: '0.3rem'}} />
        <InfoBubble className="primary" style={{margin: 'auto', width: '26rem'}}>
          <InfoIcon style={{color: `${theme.text.secondary.primary}`, margin: 'auto 1rem auto 0'}} />
          <Typography className="normal secondary">
            This information was sourced from public records.
            Records may be incomplete or innacurate.
          </Typography>
        </InfoBubble>
      </FlexWrapper>
      <FlexWrapper className="column fitContentHeight fitContentWidth" style={{gridColumn: '1', gridRow: '3', margin: '1rem auto 0 auto'}}>
        <TreeContainer className="fitContentHeight" style={{width: '29rem'}}>
          {saveError ?
            <ErrorWrapper className="fitContentHeight" style={{marginTop: '1rem'}}>
              <ErrorIcon />
              <FlexWrapper className="column">
                <Typography>Save failed</Typography>
                <Typography>Try again or contact your</Typography>
                <Typography>App Administrator</Typography>
              </FlexWrapper>
            </ErrorWrapper>
          : existingMortgage ?
            <AlertWrapper className="caution" style={{marginTop: '1rem'}} onClick={() => handleMortgageScan(viewingMortgageFromSearch, true, true)}>
              <ErrorIcon style={{color: `${theme.accent.caution.primary}`}} />
              Property already saved. View in repository
              <OpenInNewIcon style={{color: `${theme.palette.primary.dark}`}} />
            </AlertWrapper>
          : saveSuccess || (!saveFunctionEnabled && !saveSuccess) ?
            <AlertWrapper className="success" style={{marginTop: '1rem'}} onClick={() => handleMortgageScan(viewingMortgageFromSearch, true, true)}>
              <CheckCircleIcon style={{color: `${theme.accent.success.secondary}`}} />
              Property saved. View in repository
              <OpenInNewIcon style={{color: `${theme.accent.caution.primaryMedium}`}} />
            </AlertWrapper>
          :
            <FlexWrapper style={{height: '3rem'}}></FlexWrapper>
          }

          <ButtonWrapper className="selfAlignEnd marginAuto" style={{margin: '0 auto 0.6rem auto'}}>
            <Tooltip
              componentsProps={{tooltip: {sx: {
                color: `${theme.tooltip.error.background}`,
                backgroundColor: `${theme.tooltip.error.background}`,
                border: `${theme.tooltip.error.background}`,
                borderBottom: `${theme.tooltip.error.background}`,
                opacity: '0',
              }}}}
              PopperProps={{modifiers: [{
                name: "offset",
                options: {
                  offset: [0, -7],
                },
              }]}}
              title={!saveFromSearchPerm ?
              <FlexWrapper>
                <span style={{padding: '1rem', margin: '0', fontWeight: '600', textAlign: 'center'}}>
                  Elevated authority required
                </span>
              </FlexWrapper>
            :
                ""
              }
              placement="top"
              arrow
            >
              <span>
                <Button className={searchFetching ? "primaryLoading" : (saveFunctionEnabled && saveFromSearchPerm) ? "primary" : "primaryDisabled"} onClick={(saveFunctionEnabled && saveFromSearchPerm) ? () => handleSaveProperty() : null} style={{width: '23rem'}}>
                  {searchFetching ?
                    <CircularProgress
                      sx={{ color: `${theme.palette.common.white}` }}
                      size={18}
                    />
                  :
                    <>
                      <SaveIcon style={{marginRight: '0.5rem'}} />
                      Save Property
                    </>
                  }
                </Button>
              </span>
            </Tooltip>
          </ButtonWrapper>
                
          <FlexWrapper style={{justifyContent: 'center'}}>
            <Divider style={{width: '10rem'}} />
            <AccentInfo style={{width: '4.5rem', margin: '0'}}>-or-</AccentInfo>
            <Divider style={{width: '10rem'}} />
          </FlexWrapper>

          <FlexWrapper className="column" style={{width: '100%', height: 'fit-content', padding: '0 1rem'}}>
            {responseError || propertyNotFound || nullForm ?
              <StatusAndLabelWrapper style={{width: 'fit-content', margin: '0 auto'}}>
                <ErrorWrapper>
                  {responseError ?
                    <>
                      <ErrorIcon />
                      Your search encountered an error. Please try again.
                    </>
                  : nullForm ?
                    <>
                      <ErrorIcon />
                      Empty form.
                    </>
                  : allFieldsPresent ?
                    <>
                      <SignalWifiBadIcon />
                      No results found. 
                    </>
                  :
                    <>
                      <SignalWifiBadIcon />
                      Search failed. Please try again.
                    </>
                  }
                </ErrorWrapper>
              </StatusAndLabelWrapper>
            :
              <div style={{height: '3rem'}}></div>
            }
            <HeaderText style={{margin: '0.5rem auto 0 auto'}}>
              Perform another search
            </HeaderText>
            <InputWrapper style={{width: '23rem', margin: '0 auto'}}>
              <StatusAndLabelWrapper className="justifyStart">
                <Label htmlFor="streetAddress">Street Address</Label>
                {(statusStreetAddress === "invalid" || statusCity === "invalid" || statusState === "invalid" || statusPostalCode === "invalid") && (
                  <ErrorWrapper>
                    <ErrorIcon />
                    Invalid Entries
                  </ErrorWrapper>
                )}
              </StatusAndLabelWrapper>
              <StyledInputElement
                style={{width: '23rem'}}
                disabled={searchFetching || !searchForPropertyPerm}
                autoComplete="off"
                type="text"
                name="streetAddress"
                id="streetAddress"
                ref={streetAddress}
                className={statusStreetAddress === "invalid" ? 'error' : searchForPropertyPerm ? '' : 'disabled'}
                onChange={() => {
                  if (statusStreetAddress === "invalid") {
                    setStatusStreetAddress(null)
                  }
                  if (nullForm) {
                      setNullForm(false)
                    }
                }}
              />
            </InputWrapper>
            <FlexWrapper style={{width: 'fit-content', margin: '0 auto'}}>
              <InputWrapper style={{width: '18rem'}}>
                <StatusAndLabelWrapper className="column">
                  <Label htmlFor="city">City</Label>
                </StatusAndLabelWrapper>
                <StyledInputElement
                  style={{width: '18rem'}}
                  disabled={searchFetching || !searchForPropertyPerm}
                  autoComplete="off"
                  type="text"
                  name="city"
                  id="city"
                  ref={city}
                  className={statusCity === "invalid" ? 'error' : searchForPropertyPerm ? '' : 'disabled'}
                  onChange={() => {
                    if (statusCity === "invalid") {
                      setStatusCity(null)
                    }
                    if (nullForm) {
                      setNullForm(false)
                    }
                  }}
                />
              </InputWrapper>
            </FlexWrapper>
            <FlexWrapper style={{width: 'fit-content', margin: '0 auto'}}>
              <InputWrapper style={{width: '6rem', marginRight: '0.5rem'}}>
                <StatusAndLabelWrapper className="column">
                  <Label htmlFor="state">State (XX)</Label>
                </StatusAndLabelWrapper>
                <StyledInputElement
                  style={{width: '6rem'}}
                  disabled={searchFetching || !searchForPropertyPerm}
                  autoComplete="off"
                  type="text"
                  name="state"
                  id="state"
                  ref={state}
                  className={statusState === "invalid" ? 'error' : searchForPropertyPerm ? '' : 'disabled'}
                  onChange={() => {
                    if (statusState === "invalid") {
                      setStatusState(null)
                    }
                    if (nullForm) {
                      setNullForm(false)
                    }
                  }}
                />
              </InputWrapper>
              <InputWrapper style={{width: '8rem', marginLeft: '0.5rem'}}>
                <StatusAndLabelWrapper className="column">
                  <Label htmlFor="postalCode">Zip Code</Label>
                </StatusAndLabelWrapper>
                <StyledInputElement
                  style={{width: '8rem'}}
                  disabled={searchFetching || !searchForPropertyPerm}
                  autoComplete="off"
                  type="text"
                  name="postalCode"
                  id="postalCode"
                  ref={postalCode}
                  className={statusPostalCode === "invalid" ? 'error' : searchForPropertyPerm ? '' : 'disabled'}
                  onChange={() => {
                    if (statusPostalCode === "invalid") {
                      ssetStatusPostalCode(null)
                    }
                    if (nullForm) {
                      setNullForm(false)
                    }
                  }}
                />
              </InputWrapper>
            </FlexWrapper>

            <ButtonWrapper className="selfAlignEnd marginAuto" style={{margin: '1.5rem auto 2.5rem auto'}}>
            <Tooltip
                componentsProps={{tooltip: {sx: {
                  color: `${theme.tooltip.caution.color}`,
                  backgroundColor: `${theme.tooltip.caution.background}`,
                  border: `0.1rem solid ${theme.tooltip.caution.border}`,
                  borderBottom: `0.1rem solid ${theme.tooltip.caution.border}`,
                  opacity: '0',
                  borderRadius: '1rem',
                  width: 'fit-content',
                  minWidth: 'fit-content',
                  padding: '0.5rem 1rem',
                }}}}
                PopperProps={{modifiers: [{
                  name: "offset",
                  options: {
                    offset: [0, 0],
                  },
                }]}}
                title={(remainingMonthlyQueries === 0) ?
                  <FlexWrapper className="column">
                    <Typography className="bold alignTextCenter">
                      No monthly queries remaining.
                    </Typography>
                  </FlexWrapper>
                : !searchForPropertyPerm ?
                  <FlexWrapper className="column">
                    <Typography className="bold alignTextCenter">
                      Elevated authority required
                    </Typography>
                  </FlexWrapper>
                : null} 
                placement="top"
                arrow
              >
                <span>
                  <Button 
                    className={searchFetching ? 'neutralLoading' : (!searchForPropertyPerm || nullForm || responseError || remainingMonthlyQueries === 0) ? "neutralDisabled" : "neutral"}
                    disabled={searchFetching || !searchForPropertyPerm || nullForm || responseError || remainingMonthlyQueries === 0}
                    onClick={searchFetching || !searchForPropertyPerm || nullForm || responseError || remainingMonthlyQueries === 0 ? null : () => validateSearch()}
                  >
                    {searchFetching ?
                      <CircularProgress
                        sx={{ color: `${theme.button.text.neutralLoading}` }}
                        size={18}
                      />
                    :
                      <>
                        <SearchIcon style={{marginRight: '0.2rem'}} />
                        Search
                      </>
                    }
                  </Button>
                </span>
              </Tooltip>
            </ButtonWrapper>
          </FlexWrapper>
          {userRole === 'super' ?
            <FlexWrapper className="alignCenter justifyCenter">
              <MiscellaneousServicesIcon style={{height: '3rem', width: '3rem', color: `${theme.icon.neutral.primary}`}} />
              <Typography>View all fields:</Typography>
              <Switch checked={showAll} onChange={(e) => handleShowAll(e)} />
            </FlexWrapper>
          :
            <></>
          }
        </TreeContainer>
        <Button
          style={{width: '12rem'}}
          className={searchFetching ? 'outlinedNeutralLoading' : "outlinedNeutral"}
          disabled={searchFetching}
          onClick={searchFetching ? null : () => dispatch( clearPropertySearchResponse() )}
        >
          <UndoIcon style={{marginRight: '0.2rem'}} />
          Clear results
        </Button>
      </FlexWrapper>
    </>
  )
}

export default SearchSideBar;